import { AppState } from "./system/AppState";
import { EventSource } from "./system/EventSource";

export class AppBackgroundManager {
  public constructor(private appState: AppState) {
    document.addEventListener("pause", this.handlePauseEvent);
    document.addEventListener("resume", this.handleResumeEvent);
  }

  public close() {
    document.removeEventListener("pause", this.handlePauseEvent);
    document.removeEventListener("resume", this.handleResumeEvent);
  }

  /**
   * Should be called just before calling out to functionality that switches to another
   * Android Activity.
   *
   * (for example, when retreiving a photo using cordova-plugin-camera
   *
   * Must be matched with a call to [[popExternalActivity]]
   *
   * See: https://cordova.apache.org/docs/en/latest/cordova/events/events.html#pause
   *      https://cordova.apache.org/docs/en/latest/guide/platforms/android/index.html#lifecycle-guide
   */
  public pushExternalActivity() {
    this.externalActivityCounter++;
  }

  /**
   * Must be called after [[pushExternalActivity]], in the callback that returns
   */
  public popExternalActivity() {
    if (this.externalActivityCounter === 0) {
      throw new Error("Mismatched call to popExternalActivity");
    }

    this.externalActivityCounter--;
  }

  private externalActivityCounter = 0;

  private inExternalActivity(): boolean {
    return this.externalActivityCounter > 0;
  }

  handlePauseEvent = () => {
    if (!this.inExternalActivity()) {
      this.appState.realTimeDisconnect();
      this.pausedAt = new Date();
      this.onPause.broadcast(undefined);
    }
  };

  handleResumeEvent = () => {
    if (!this.inExternalActivity()) {
      this.appState.realTimeReconnect();

      if (this.pausedAt !== null) {
        const backgroundMillis = new Date().getTime() - this.pausedAt.getTime();
        this.onResume.broadcast(backgroundMillis);
      } else {
        // It's not clear how a resume event can happen before a pause event.
        // But yet it seems to happen (on both Android and iOS)
        this.onResume.broadcast(0);
      }
    }
  };

  private pausedAt: Date | null = null;

  public readonly onPause = new EventSource<void>();

  /**
   * The event payload is the amount of time the app was in the background (in milliseconds)
   */
  public readonly onResume = new EventSource<number>();
}
